import { createFileRoute, Link } from "@tanstack/react-router";
import { PageSection } from "@/components/site/PageSection";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { esHead, esWebPageSchema } from "@/lib/es-pages";
import { EsHero, EsHomeDoctorFigure } from "@/components/site/EsHomeHero";
import { heroPreloadLink } from "@/components/site/Hero";
import { ES_PAGE_MEDIA, procedureImageObjects } from "@/lib/procedure-media";

export const Route = createFileRoute("/es/")({
  head: () => {
    const base = esHead("home");
    return {
      ...base,
      links: [heroPreloadLink, ...(base.links ?? [])],
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify(esWebPageSchema("home")),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": procedureImageObjects(
              ES_PAGE_MEDIA["/es"],
              "es",
              "https://virginiabariatric.com/es",
            ),
          }),
        },
      ] : [],
    };
  },
  component: EsHome,
});

const opciones = [
  {
    to: "/es/manga-gastrica",
    titulo: "Manga gástrica",
    texto:
      "La operación de pérdida de peso más realizada. Se reduce el tamaño del estómago por laparoscopía, sin desviar el intestino.",
  },
  {
    to: "/es/bypass-gastrico",
    titulo: "Bypass gástrico",
    texto:
      "El Roux-en-Y combina un estómago más pequeño con un cambio en el recorrido de los alimentos. Muy eficaz contra la diabetes tipo 2 y el reflujo.",
  },
  {
    to: "/es/perdida-de-peso-con-medicamentos",
    titulo: "Medicamentos para bajar de peso",
    texto:
      "Wegovy, Zepbound, Ozempic y Mounjaro bajo supervisión médica, con seguimiento nutricional y visitas por telemedicina.",
  },
  {
    to: "/es/cobertura-de-seguro",
    titulo: "Cobertura de seguro",
    texto:
      "Requisitos de las aseguradoras en Virginia, qué documentos necesita y qué hacer si le niegan la cobertura.",
  },
] as const;

function EsHome() {
  return (
    <EsPageShell page="home" hero={<EsHero />}>
      <PageSection className="pt-2 pb-4" maxWidth="4xl">
        <p className="mx-auto max-w-3xl text-center text-muted-foreground">
          El Dr. Matthew Fitzer es cirujano bariátrico certificado y ha realizado{" "}
          <Link to="/es/cirugia-bariatrica-virginia" className="font-medium text-brand-logo hover:underline">
            más de 6,000 operaciones laparoscópicas de pérdida de peso
          </Link>{" "}
          desde 2005. Nuestro consultorio está en Herndon, Virginia, y atendemos a pacientes de
          Reston, Fairfax, Arlington, Alexandria, el condado de Loudoun y el área metropolitana de
          Washington, D.C.
        </p>
        <div className="mt-6">
          <EsHomeDoctorFigure />
        </div>
      </PageSection>

      <PageSection className="py-4" maxWidth="4xl">
        <h2 className="text-center font-serif text-2xl text-brand-deep">
          Sus opciones para bajar de peso
        </h2>
        <ul className="mt-4 grid gap-4 sm:grid-cols-2">
          {opciones.map((o) => (
            <li key={o.to}>
              <Link
                to={o.to}
                className="flex h-full flex-col rounded-2xl border border-border bg-card p-5 transition-colors hover:border-brand-deep"
              >
                <span className="font-serif text-xl text-brand-deep">{o.titulo}</span>
                <span className="mt-2 text-sm text-foreground/80">{o.texto}</span>
              </Link>
            </li>
          ))}
        </ul>
      </PageSection>

      <EsSection heading="¿Por qué elegir a Virginia Bariatric Surgery?">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>Experiencia:</strong>{" "}
            <Link to="/es/cirugia-bariatrica-virginia" className="font-medium text-brand-logo hover:underline">
              más de 6,000 operaciones laparoscópicas de pérdida de peso
            </Link>{" "}
            realizadas por el Dr. Fitzer desde 2005.
          </li>
          <li>
            <strong>Un solo cirujano de principio a fin:</strong> el mismo médico lo evalúa, lo
            opera y lo acompaña en el seguimiento.
          </li>
          <li>
            <strong>Programa completo:</strong> nutrición, apoyo conductual, grupo de apoyo y
            control a largo plazo, no solo la operación.
          </li>
          <li>
            <strong>Opciones sin cirugía:</strong> si todavía no quiere operarse, ofrecemos un{" "}
            <Link to="/es/medicamentos-para-bajar-de-peso" className="font-medium text-brand-logo hover:underline">
              programa médico con medicamentos GLP-1
            </Link>{" "}
            supervisado por un médico.
          </li>
        </ul>
      </EsSection>

      <EsSection heading="¿Califico para una cirugía de pérdida de peso?">
        <p>
          En términos generales, los criterios aceptados en los Estados Unidos incluyen un índice
          de masa corporal (IMC) de 40 o más, o un IMC de 35 o más acompañado de un problema de
          salud relacionado con el peso, como diabetes tipo 2, apnea del sueño, presión arterial
          alta o enfermedad por reflujo. Algunos planes de seguro y programas de pago propio
          consideran candidatos con un IMC más bajo.
        </p>
        <p>
          Cada aseguradora define sus propios requisitos y suele pedir documentación de intentos
          previos de bajar de peso. La única forma de saber con certeza si califica es una
          evaluación en el consultorio. La primera consulta es el momento de revisar su historia
          clínica, su peso, sus medicamentos y su cobertura.
        </p>
      </EsSection>

      <EsSection heading="Cómo empezar">
        <ol className="list-decimal space-y-2 pl-5">
          <li>
            Llame al{" "}
            <a href="tel:7037099771" className="font-medium text-brand-logo hover:underline">
              (703) 709-9771
            </a>{" "}
            o{" "}
            <Link to="/es/contacto" className="font-medium text-brand-logo hover:underline">
              envíenos un mensaje
            </Link>
            .
          </li>
          <li>Verificamos su seguro y le explicamos los requisitos de su plan.</li>
          <li>Consulta con el Dr. Fitzer para elegir el tratamiento adecuado.</li>
          <li>Preparación, procedimiento y seguimiento con nuestro equipo.</li>
        </ol>
        <p className="text-sm text-muted-foreground">
          Esta información es educativa y no sustituye el consejo médico personalizado.
        </p>
      </EsSection>
    </EsPageShell>
  );
}
